"use client";

import { Ticket, Flame } from "lucide-react";
import { motion } from "motion/react";
import Link from "next/link";
import { formatCurrency, cleanHeaderTags } from "@/_helper/helper";
import TicketImageSlider from "./TicketImageSlider";

interface TicketCardProps {
  ticket: any;
  index: number;
}

export default function TicketCard({ ticket, index }: TicketCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="relative group h-full"
    >
      <div
        className={`relative flex flex-col h-full bg-white rounded-xl overflow-hidden transition-all duration-500 shadow-xl hover:shadow-2xl ${ticket.IsPopular ? "border border-orange-500 ring-1 ring-orange-500/20" : ""}`}
      >
        {ticket.IsPopular && (
          <div className="absolute top-0 right-0 z-10">
            <div className="bg-gradient-to-br from-orange-500 to-amber-600 text-white px-4 py-1 text-[10px] font-bold uppercase tracking-wider shadow-lg rounded-bl-lg flex items-center gap-1">
              <Flame className="w-3.5 h-3.5 fill-white animate-pulse" /> Highest
              Selling
            </div>
          </div>
        )}

        <div className="p-4 py-6 flex flex-col flex-grow space-y-3">
          <div className="flex items-start gap-3">
            <div
              className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${ticket.IsPopular ? "bg-gradient-to-br from-orange-500 to-amber-600" : "bg-gradient-to-br from-gray-700 to-gray-900"}`}
            >
              <Ticket className="w-6 h-6 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <Link
                className="flex flex-col gap-1"
                href={`/sou-tickets/${ticket?.TicketURL}`}
              >
                <h3 className="text-base font-bold text-gray-900 leading-tight">
                  {ticket.Title}
                </h3>
                {ticket.LastEntry && (
                  <h3 className="text-sm text-orange-500 leading-tight">
                    Last Entry : {ticket.LastEntry}
                  </h3>
                )}
              </Link>
            </div>
          </div>

          <TicketImageSlider
            images={[
              ...(ticket?.ImageUpload ? [{ Image: ticket?.ImageUpload }] : []),
              ...(ticket?.SOUTicketImage && ticket?.SOUTicketImage?.length > 0
                ? ticket?.SOUTicketImage?.map((item: any) => ({
                    Image: item?.Image,
                  }))
                : []),
            ]}
          />

          <div
            className="text-xs text-gray-600 leading-snug ticket-desc line-clamp-4 blog-content"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(ticket.Description),
            }}
          />

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 space-y-2 border border-gray-200 mt-auto">
            <div className="flex items-center justify-between">
              {ticket?.PerPersonRate > 0 && (
                <div className="flex-1 flex-col text-center">
                  <span className="font-medium text-[10px] md:text-xs block">
                    Per Person
                  </span>
                  {ticket?.PerPersonRate && +ticket?.PerPersonRate > 0 && (
                    <span className="text-base md:text-xl font-bold text-orange-500 block">
                      {formatCurrency(ticket?.PerPersonRate || 0)}
                    </span>
                  )}
                </div>
              )}
              {ticket?.ChildSevenYrRate > 0 && (
                <div className="flex-1 flex-col text-center">
                  <span className="font-medium text-[10px] md:text-xs block">
                    Child <span className="text-nowrap">(3yr - 15yr)</span>
                  </span>
                  {ticket?.ChildSevenYrRate &&
                    +ticket?.ChildSevenYrRate > 0 && (
                      <span className="text-base md:text-xl font-bold text-orange-500 block">
                        {formatCurrency(ticket?.ChildSevenYrRate || 0)}
                      </span>
                    )}
                </div>
              )}
              {ticket?.ChildThreeYrRate > 0 && (
                <div className="flex-1 flex-col text-center">
                  <span className="font-medium text-[10px] md:text-xs block">
                    Child <span className="text-nowrap">(3yr - 6yr)</span>
                  </span>
                  {ticket?.ChildThreeYrRate &&
                    +ticket?.ChildThreeYrRate > 0 && (
                      <span className="text-base md:text-xl font-bold text-orange-500 block">
                        {formatCurrency(ticket?.ChildThreeYrRate || 0)}
                      </span>
                    )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Link
              href={`/sou-tickets/${ticket?.TicketURL}`}
              className={`block text-center flex-1 py-2 border-2 font-semibold rounded-lg transition-all duration-300 ${ticket.IsPopular ? "text-amber-600 border-amber-500 hover:bg-amber-50" : "text-gray-700 border-gray-500 hover:bg-gray-50"}`}
            >
              View Details
            </Link>
            <Link
              href={`/cart?ticketId=${ticket?.ID}`}
              className={`block text-center flex-1 py-2 text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${ticket.IsPopular ? "bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:shadow-orange-300 cursor-pointer" : "bg-gradient-to-r from-gray-700 to-gray-900 text-white hover:shadow-gray-300 cursor-pointer"}`}
            >
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
